import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useListSprintsQuery,
  useListTasksQuery,
  useListDailyGoalsQuery,
  useListCallTopicsQuery,
  useListRefinementsQuery,
  useListScopeItemsQuery,
} from '../redux/firestoreApi';
import {
  generateMarkdownReport,
  generateTextReport,
} from '../reports/generateReport';
import { showToast } from '../redux/uiSlice';
import PageHeader from '../components/common/PageHeader';

export default function ReportPage() {
  const uid = useAppSelector((s) => s.session.uid)!;
  const projectId = useAppSelector((s) => s.session.activeProjectId);
  const sprintId = useAppSelector((s) => s.session.activeSprintId);
  const dispatch = useAppDispatch();
  const [format, setFormat] = useState<'text' | 'markdown'>('text');

  const sprintsQ = useListSprintsQuery(
    projectId ? { uid, projectId } : (undefined as any),
    { skip: !projectId },
  );
  const tasksQ = useListTasksQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const goalsQ = useListDailyGoalsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const topicsQ = useListCallTopicsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const refinementsQ = useListRefinementsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );
  const scopeQ = useListScopeItemsQuery(
    projectId && sprintId ? { uid, projectId, sprintId } : (undefined as any),
    { skip: !projectId || !sprintId },
  );

  const sprint = useMemo(
    () => sprintsQ.data?.find((s) => s.id === sprintId) ?? null,
    [sprintsQ.data, sprintId],
  );

  const ctx = useMemo(
    () => ({
      sprint,
      tasks: tasksQ.data ?? [],
      dailyGoals: goalsQ.data ?? [],
      callTopics: topicsQ.data ?? [],
      refinements: refinementsQ.data ?? [],
      scopeItems: scopeQ.data ?? [],
    }),
    [sprint, tasksQ.data, goalsQ.data, topicsQ.data, refinementsQ.data, scopeQ.data],
  );

  const report = useMemo(
    () => (format === 'text' ? generateTextReport(ctx) : generateMarkdownReport(ctx)),
    [ctx, format],
  );

  if (!projectId || !sprintId) {
    return (
      <>
        <PageHeader title="Reports" />
        <Alert severity="info">Select a project and sprint to generate a report.</Alert>
      </>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(report);
      dispatch(showToast({ message: 'Copied to clipboard', severity: 'success' }));
    } catch {
      dispatch(showToast({ message: 'Copy failed', severity: 'error' }));
    }
  };

  const baseName = (sprint?.sprintName ?? 'sprint-report').replace(/\s+/g, '_');

  const downloadFile = () => {
    const ext = format === 'text' ? 'txt' : 'md';
    const mime = format === 'text' ? 'text/plain' : 'text/markdown';
    saveAs(new Blob([report], { type: `${mime};charset=utf-8` }), `${baseName}.${ext}`);
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const lines = doc.splitTextToSize(report, 515);
    doc.setFont('courier');
    doc.setFontSize(10);
    let y = 40;
    const pageHeight = doc.internal.pageSize.getHeight();
    for (const line of lines) {
      if (y > pageHeight - 40) {
        doc.addPage();
        y = 40;
      }
      doc.text(line, 40, y);
      y += 12;
    }
    doc.save(`${baseName}.pdf`);
  };

  return (
    <>
      <PageHeader
        title="Sprint Report"
        subtitle={sprint?.sprintName ?? ''}
        actions={
          <ButtonGroup variant="contained">
            <Button startIcon={<ContentCopyIcon />} onClick={copy}>
              Copy
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={downloadFile}>
              Download {format === 'text' ? '.txt' : '.md'}
            </Button>
            <Button startIcon={<PictureAsPdfIcon />} onClick={downloadPdf}>
              PDF
            </Button>
          </ButtonGroup>
        }
      />

      <Paper variant="outlined">
        <Tabs
          value={format}
          onChange={(_, v) => setFormat(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab value="text" label="Plain text" />
          <Tab value="markdown" label="Markdown" />
        </Tabs>
        <Box sx={{ p: 3 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: 'pre-wrap',
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              lineHeight: 1.6,
              maxHeight: '70vh',
              overflow: 'auto',
            }}
          >
            {report}
          </Box>
          {report.trim() === '' && (
            <Typography color="text.secondary">Nothing to report yet.</Typography>
          )}
        </Box>
      </Paper>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Tip: tasks are auto-grouped by assignee and ordered by status.
        </Typography>
      </Stack>
    </>
  );
}
